import {
  Activity,
  BarChart3,
  BedDouble,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  CloudOff,
  FileBarChart,
  FlaskConical,
  HeartPulse,
  LayoutDashboard,
  ListOrdered,
  Pill,
  Receipt,
  ScanLine,
  Send,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Stethoscope,
  UserCog,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** any of these permissions shows the item */
  perms?: string[]
  module?: string
  end?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/app/analytics', label: 'Hospital analytics', icon: BarChart3, perms: ['analytics.executive'] },
      { to: '/app/approvals', label: 'Approvals & tasks', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Patient care',
    items: [
      { to: '/app/patients', label: 'Patients', icon: Users, perms: ['patients.read'] },
      { to: '/app/offline', label: 'Offline records', icon: CloudOff, perms: ['patients.write', 'appointments.manage'] },
      { to: '/app/appointments', label: 'Appointments', icon: CalendarDays, perms: ['appointments.read'], module: 'appointments' },
      { to: '/app/queue', label: 'OPD queue', icon: ListOrdered, perms: ['appointments.read', 'clinical.write'], module: 'appointments' },
      { to: '/app/opd', label: 'Consultations', icon: Stethoscope, perms: ['clinical.read'], module: 'opd' },
      { to: '/app/referrals', label: 'Referrals', icon: Send, perms: ['clinical.read'], module: 'opd' },
      { to: '/app/ipd', label: 'Inpatients & beds', icon: BedDouble, perms: ['ipd.read'], module: 'ipd' },
      { to: '/app/nursing', label: 'Nursing station', icon: HeartPulse, perms: ['nursing.write'], module: 'nursing' },
    ],
  },
  {
    label: 'Diagnostics',
    items: [
      { to: '/app/lab', label: 'Laboratory', icon: FlaskConical, perms: ['lab.read'], module: 'lab' },
      { to: '/app/radiology', label: 'Radiology', icon: ScanLine, perms: ['radiology.read'], module: 'radiology' },
    ],
  },
  {
    label: 'Pharmacy & stores',
    items: [
      { to: '/app/pharmacy', label: 'Pharmacy', icon: Pill, perms: ['pharmacy.read'], module: 'pharmacy' },
      { to: '/app/procurement', label: 'Procurement', icon: ShoppingCart, perms: ['procurement.read'], module: 'procurement' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/app/billing', label: 'Billing & payments', icon: Receipt, perms: ['billing.read'] },
      { to: '/app/insurance', label: 'Insurance claims', icon: ShieldCheck, perms: ['insurance.read'], module: 'insurance' },
    ],
  },
  {
    label: 'Organization',
    items: [
      { to: '/app/hr', label: 'Staff & rosters', icon: UsersRound, perms: ['hr.read'], module: 'hr' },
      { to: '/app/reports', label: 'Reports', icon: FileBarChart, perms: ['reports.view'] },
      { to: '/app/admin/users', label: 'Users & roles', icon: UserCog, perms: ['admin.users', 'admin.roles'] },
      { to: '/app/admin/facilities', label: 'Facilities & catalog', icon: Building2, perms: ['admin.facilities', 'admin.catalog', 'beds.manage', 'lab.catalog', 'pharmacy.catalog'] },
      { to: '/app/admin/settings', label: 'Hospital settings', icon: Settings, perms: ['admin.settings'] },
      { to: '/app/admin/audit', label: 'Audit trail', icon: Activity, perms: ['audit.view'] },
    ],
  },
  {
    label: 'Help',
    items: [{ to: '/app/resources', label: 'Resources', icon: BookOpen }],
  },
]

export function visibleNav(canAny: (...p: string[]) => boolean, hasModule: (m: string) => boolean): NavGroup[] {
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => (!i.perms || canAny(...i.perms)) && (!i.module || hasModule(i.module))),
  })).filter((g) => g.items.length > 0)
}
